import type { ReactElement } from 'react';
import { useState } from 'react';
import { toCurrency } from 'utils/numbers';

import SkuTags from 'components/shared/SkuTags';
import CartProduct from 'models/CartProduct';
import SaveOrDiscardModal from 'components/Cart/Header/SaveOrDiscardModal';
import RecipePrice from './RecipePrice';

type Props = {
  cartId: number;
  cartProduct: CartProduct;
  hasCartChanged: boolean;
  hasCartError: boolean;
};

function RecipeContent({
  cartProduct,
  cartId,
  hasCartChanged,
  hasCartError,
}: Props): ReactElement {
  const { currentItem, hasServingOptions, isSoldOut, isPremiumItem } = cartProduct;

  const {
    isSnack,
    isGroceryCategory,
    isPreparedMeal,
    quantity,
  } = currentItem;

  const {
    tags,
    title,
    brand,
    subtitle,
    calories,
    id: skuId,
    link_to_product: linkToProduct,
    prep_and_cook_time: prepAndCookTime,
    subscription_price_per_serving: subscriptionPricePerServing,
    is_lto_box: isLTOBox,
  } = currentItem.sku;

  const [
    showSaveOrDiscardModal,
    setShowSaveOrDiscardModal,
  ] = useState(false);
  const linkWithCartId = isLTOBox ? '/lto/thanksgiving' : `${linkToProduct}?cart_id=${cartId}`;

  const handleRecipeClick = (e) => {
    if (hasCartChanged && hasCartError) {
      e.preventDefault();
      setShowSaveOrDiscardModal(true);
    }
  };

  const handleDiscard = () => {
    window.location.href = linkWithCartId;
  };

  return (
    <>
      <div className="c-recipe__content--center">
        <div className="c-recipe__type">
          {isSnack && `${brand}®`}
        </div>

        <a
          id={`recipe-title-${skuId}`}
          className="c-recipe__title"
          href={linkWithCartId}
          onClick={(e) => handleRecipeClick(e)}
        >
          <h3>
            {title}
            <span className="c-recipe__subtitle">
              {subtitle}
            </span>
          </h3>
        </a>
      </div>

      <div className="c-recipe__content--bottom">
        {!isSoldOut && currentItem.hasLowAvailability && (
          <div className="c-recipe__tags">
            Only a few left
          </div>
        )}

        {!(isGroceryCategory) && !!tags.length && (<SkuTags tags={tags} />)}

        <div className="c-recipe__info">
          {!(isGroceryCategory) && (
            <>
              {!!prepAndCookTime && (
              <>
                <span className="c-recipe__time">
                  {`${prepAndCookTime} min`}
                </span>
                {isPreparedMeal ? ' • ' : ' / '}
              </>
              )}

              {!!calories && (
              <span className="c-recipe__calories">
                {!!calories && `${calories} cal`}
              </span>
              )}

              {(!hasServingOptions || (hasServingOptions && !!quantity)) && (
              <>
                {!!calories && (isPreparedMeal ? ' • ' : ' / ')}
                <span className="c-recipe__price">
                  <span style={{
                    ...(isPremiumItem
                      ? { color: '#31544E', fontWeight: 700 } : {}),
                  }}
                  >
                    {toCurrency(subscriptionPricePerServing)}
                  </span>
                  {' '}
                  per serving
                </span>
              </>
              )}
            </>
          )}

          {(isGroceryCategory) && (
            <>
              <RecipePrice
                sku={currentItem.sku}
                      // @ts-expect-error TODO: fix null safety
                price={null}
              />
            </>
          )}
        </div>
      </div>

      {showSaveOrDiscardModal && (
        <SaveOrDiscardModal
          onClose={() => setShowSaveOrDiscardModal(false)}
          onDiscard={handleDiscard}
          viewRecipeLink={linkWithCartId}
        />
      )}
    </>
  );
}

export default RecipeContent;
